<template>
  <div class="multi" :class="{ 'multi--open': showDropdown }" ref="multi" tabindex="0" @focus="showDropdown = true">
    <div class="multi__placeholder input input--default input--shadow" ref="multiPlaceholder">
      {{ placeholder }}
    </div>
    <div class="multi__dropdown" v-if="showDropdown" :style="dropdownStyleProperties">
      <input type="text" @focus="showDropdown = true" @focusout="showDropdown = false" @keydown.enter.prevent tabindex="0"
        class="multi__search input input--default" v-model="searchKeywords" />
      <div class="multi__items">
        <div class="multi__item">
          <div class="checkbox">
            <input type="checkbox" id="select-all" class="checkbox__checkbox" @input="toggleSelectAll"
              :checked="allSelected" tabindex="-1" />
            <label for="select-all" class="checkbox__label"></label>
          </div>
          <label for="select-all">
            {{ allSelected ? "Deselect All" : "Select All" }}
          </label>
        </div>

        <div class="multi__item" v-for="item in filteredItems" :key="item.id">
          <div class="checkbox">
            <input type="checkbox" :id="item.id" class="checkbox__checkbox" @input="toggleItems(item)"
              :checked="selectedItems.find((i) => i.id === item.id)" tabindex="-1" />
            <label :for="item.id" class="checkbox__label"></label>
          </div>
          <label :for="item.id">
            {{ customLabel ? customLabel(item) : item[label] }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  items: {
    required: true,
    type: Array,
  },
  label: {
    required: false,
    type: String,
  },
  placeholder: {
    required: true,
    type: String,
  },
  customLabel: {
    required: false,
    type: Function,
  },
  reduce: {
    required: false,
    type: Function,
  },
  selected: {
    required: false,
    type: Array,
  },
});

const emit = defineEmits(["input"]);

const dropdownWidth = ref(0);
const dropdownX = ref(0);
const dropdownY = ref(0);
const showDropdown = ref(false);
const searchKeywords = ref("");
const selectedItems = ref([]);

// Template refs
const multi = ref(null);
const multiPlaceholder = ref(null);

const dropdownStyleProperties = computed(() => {
  return {
    width: `${dropdownWidth.value}px`,
    left: `${dropdownX.value}px`,
    top: `${dropdownY.value}px`,
  };
});

const filteredItems = computed(() => {
  return props.items.filter((item) =>
    (!props.label ? props.customLabel(item) : item[props.label])
      .toLowerCase()
      .includes(searchKeywords.value.toLowerCase())
  );
});

const allSelected = computed(() => {
  return selectedItems.value.length === props.items.length;
});

const dropdownEvent = (event) => {
  if ([...multi.value.getElementsByTagName("*")].includes(event.target)) {
    showDropdown.value = true;
  } else {
    showDropdown.value = false;
  }
};

const computeDropdownPosition = () => {
  dropdownX.value = multiPlaceholder.value.getBoundingClientRect().x;
  dropdownY.value = multiPlaceholder.value.getBoundingClientRect().y + 45;
};

const toggleItems = (item) => {
  const itemIndex = selectedItems.value.findIndex((i) => i.id === item.id);
  if (itemIndex === -1) {
    selectedItems.value = [...selectedItems.value, item];
  } else {
    selectedItems.value.splice(itemIndex, 1);
  }
  emitSelection();
};

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedItems.value = [];
  } else {
    selectedItems.value = [...props.items];
  }
  emitSelection();
};

const emitSelection = () => {
  if (props.reduce) {
    const reducedItems = selectedItems.value.map((item) => props.reduce(item));
    emit("input", reducedItems);
  } else {
    emit("input", selectedItems.value);
  }
};

// Watch for selected prop changes
watch(
  () => props.selected,
  (newSelected) => {
    if (newSelected) {
      selectedItems.value = newSelected.map((i) =>
        props.items.find((item) => item.id === Number(i))
      );
    }
  },
  { immediate: true }
);

onMounted(() => {
  dropdownWidth.value = multiPlaceholder.value.clientWidth;
  computeDropdownPosition();
  window.addEventListener("click", dropdownEvent);
});

onUnmounted(() => {
  window.removeEventListener("click", dropdownEvent);
});
</script>

<style lang="scss" scoped>
.multi {
  &__dropdown {
    position: fixed; 
    height: 205px;
    background: var(--bg-1);
    z-index: 99;
    box-shadow: 0px 9px 21px var(--shadow-12);
    border-radius: var(--radius-1);
    overflow: auto;
    padding: 10px;
  }

  &__placeholder,
  &__item label {
    cursor: pointer;
  }

  &__placeholder {
    font-family: var(--muli-text-semi-bold);
    color: var(--color-9);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      right: 10px;
      top: 7px;
      transition: all 0.3s ease;
      background: url("../assets/icons/default/Bold 2px-chevron_down.svg") no-repeat center;
    }
  }

  &__placeholder,
  &__item {
    display: flex;
    align-items: center;
  }

  &__search {
    height: 30px;
    width: 100%;
    border-radius: var(--radius-3);
    padding-left: 45px;
    background-image: url("../assets/icons/default/search_icon.svg");
    background-repeat: no-repeat;
    background-position: left 10px center;
  }

  &__item {
    margin: 10px 0;
    color: var(--text-2);
    font-size: var(--x-small);

    .checkbox {
      margin-right: 10px;
    }
  }

  &--open {
    .multi__placeholder::after {
      transform: rotateX(180deg);
    }
  }
}

.disabled-input {
  .multi__placeholder {
    color: grey !important;

    &::after {
      background: url("../assets/icons/default/Bold\ 2px-chevron_down_gray.svg") no-repeat center !important;
    }
  }
}

.multi.team-select .multi__placeholder {
  color: var(--text-8) !important;
  background-color: var(--bg-3) !important;
  box-shadow: none !important;
}

.multi.input--error .multi__placeholder {
  background-color: transparent !important;
  border-color: var(--border-9);
  color: var(--text-8) !important;
}

[data-theme="dark"] .multi {
  &__placeholder {
    box-shadow: none;
    border: 1px solid #46505b;
  }
}
</style>
